import { progressoDiario, sequenciaActual, useEventos } from '../state/eventos.ts';
import { useLessonProgress } from '../state/lessonProgress.ts';
import { pontosAExibir, porEnviar, useSincronizacao } from '../state/sincronizacao.ts';
import { useIdentidade } from '../state/identidade.ts';
import { ProgressBar } from '../components/primitives/index.tsx';
import { t } from '../i18n/index.ts';
import './painel.css';

/**
 * H-055 / RF-050 — painel do estudante: pontos no total, sequência, objectivo diário e
 * percentagem da trilha.
 *
 * RN-002 exige a sequência visível no cabeçalho, e é aqui que ela vive: é o
 * primeiro ecrã e o único fora do túnel da lição.
 *
 * Nenhum destes números é guardado. Todos saem do registo de eventos, o que
 * significa que não podem divergir entre si nem ficar desactualizados por
 * alguém se ter esquecido de os actualizar num sítio.
 */
export function PainelDeProgresso({ licoes }: { licoes: string[] }) {
  const eventos = useEventos((s) => s.eventos);
  const objectivo = useEventos((s) => s.objectivoDiarioMinutos);
  const tempoPorDia = useEventos((s) => s.tempoPorDia);
  const definirObjectivo = useEventos((s) => s.definirObjectivo);
  const progressoDeLicoes = useLessonProgress((s) => s.lessons);
  const totais = useSincronizacao((s) => s.totais);
  const identidade = useIdentidade((s) => s.identidade);

  // A autoridade sobre o total é o servidor (RNF-014): o que ele confirmou,
  // mais o que ainda cá está por enviar. Sem conta, a soma local é tudo.
  const total = pontosAExibir(eventos, totais);
  const pendentes = identidade ? porEnviar(eventos) : 0;
  const dias = sequenciaActual(eventos);
  const { feito, alvo, cumprido } = progressoDiario(tempoPorDia, objectivo);

  // Recebe identificadores e não lições inteiras: o painel só precisa de saber
  // quais existem e quais estão feitas, e pedir os pacotes de conteúdo todos
  // para contar linhas seria descarregar meio megabyte para mostrar "4/30".
  const concluidas = licoes.filter((id) => progressoDeLicoes[id]?.completedAt).length;

  return (
    <section className="painel" aria-label={t('painel.aria')}>
      <div className="painel__numeros">
        <Numero variante="moeda" valor={String(total)} rotulo={t('painel.pontos')} icone="◆" activo />
        <Numero
          variante="chama"
          valor={String(dias)}
          rotulo={t('painel.sequencia')}
          icone="▲"
          activo={dias > 0}
        />
        <Numero
          variante="licoes"
          valor={`${concluidas}/${licoes.length}`}
          rotulo={t('painel.licoes')}
          icone="✓"
          activo
        />
      </div>

      {/* RNF-013 — o que foi feito offline diz que está por sincronizar, em vez
          de parecer perdido ou, pior, parecer entregue. */}
      {pendentes > 0 ? (
        <p className="painel__pendentes" role="status">
          {t('painel.porSincronizar', { n: pendentes })}
        </p>
      ) : null}

      <div className="painel__objectivo">
        <ProgressBar
          value={Math.min(feito, alvo)}
          max={alvo}
          label={
            cumprido
              ? t('painel.objectivoFeito')
              : t('painel.objectivoFalta', { n: Math.ceil((alvo - feito) / 60) })
          }
        />

        {/* RF-042 — o objectivo é escolhido pelo utilizador, não imposto. */}
        <fieldset className="painel__escolha">
          <legend className="sr-only">{t('painel.objectivoLegenda')}</legend>
          {([5, 10, 20] as const).map((minutos) => (
            <button
              key={minutos}
              type="button"
              className="painel__minutos"
              aria-pressed={objectivo === minutos}
              onClick={() => definirObjectivo(minutos)}
            >
              {t('painel.minutos', { n: minutos })}
            </button>
          ))}
        </fieldset>
      </div>
    </section>
  );
}

/**
 * Um mostrador de recompensa: o selo com o ícone, o número grande e o rótulo.
 * O ícone é decorativo (o rótulo já nomeia o mostrador), por isso vai
 * `aria-hidden` — a informação nunca depende só da cor nem do símbolo
 * (RNF-023). A `variante` dá a cor do selo (a moeda dos pontos, a chama da
 * sequência, o verde das lições) e `activo` acende-o; um mostrador apagado
 * (sequência a zero) fica em tons neutros, sem fingir festa que não há.
 */
function Numero({
  valor,
  rotulo,
  icone,
  variante,
  activo = true,
}: {
  valor: string;
  rotulo: string;
  icone: string;
  variante: 'moeda' | 'chama' | 'licoes';
  activo?: boolean;
}) {
  return (
    <div className={`numero numero--${variante}${activo ? ' numero--activo' : ''}`}>
      <span className="numero__selo" aria-hidden="true">
        {icone}
      </span>
      <span className="numero__texto">
        <span className="numero__valor">{valor}</span>
        <span className="numero__rotulo">{rotulo}</span>
      </span>
    </div>
  );
}
